import React, { useState, useEffect, useRef } from 'react';
import './GuestReviews.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebase'; // Importamos la configuración existente de Firebase

// Configurar las funciones de Firebase
let functions;
try {
  const app = getApp(); // Usar la app ya inicializada
  functions = getFunctions(app, 'us-central1'); // Especificar la región
  console.log("GuestReviews: Firebase Functions configurado correctamente");
} catch (error) {
  console.error("GuestReviews: Error al configurar Firebase Functions:", error);
}

const GuestReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const carouselRef = useRef(null);

  // El ID del lugar proporcionado para Google Maps
  const PLACE_ID = 'ChIJJeRozaLeHZYRgTnY-OU4lJM';
  // Tu API Key de Google Maps
  const API_KEY = 'AIzaSyDxrF6-jfE_PKfMDoZrml961Qf9RQ2tx80';
  // Número máximo de reseñas a mostrar
  const MAX_REVIEWS = 8;

  // Detectar si la pantalla es móvil
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Comprobar al inicio
    checkIfMobile();
    
    // Comprobar al cambiar el tamaño de la ventana
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        if (!functions) {
          console.error("Firebase Functions no está disponible, usando reseñas estáticas");
          throw new Error("Firebase Functions no está inicializado");
        }

        console.log("Intentando obtener reseñas con placeId:", PLACE_ID);
        
        // Usar la instancia de functions que ya inicializamos
        const getGoogleReviews = httpsCallable(functions, 'getGoogleReviews');
        
        console.log("Llamando a la función Firebase getGoogleReviews...");
        const result = await getGoogleReviews({ 
          placeId: PLACE_ID,
          apiKey: API_KEY,
          language: 'es' // Solicitar reseñas en español
        });
        
        console.log("Resultado de la API:", result.data);
        
        // Si llegamos a este punto, la API fue exitosa
        console.log("Reseñas cargadas exitosamente desde Google Maps");
        
        // Actualizar la información del lugar
        setPlaceInfo({
          name: 'Hostería Futaleufú',
          rating: result.data.rating || 4.8,
          totalRatings: result.data.total || 25,
          address: 'Pedro Aguirre Cerda 393, Futaleufú, Chile'
        });
        
        // Actualizar las reseñas si hay datos disponibles
        if (result.data.reviews && result.data.reviews.length > 0) {
          console.log(`Mostrando ${result.data.reviews.length} reseñas reales de Google`);
          setReviews(result.data.reviews);
        } else {
          console.log("No se encontraron reseñas reales, usando reseñas estáticas");
          // Usar reseñas estáticas como respaldo
          setReviews(getStaticReviews());
        }
      } catch (err) {
        console.error("Error al obtener reseñas:", err);
        setError(err.message);
        
        // Cargar datos estáticos en caso de error
        console.log("Cargando reseñas estáticas debido al error");
        setPlaceInfo({
          name: 'Hostería Futaleufú',
          rating: 4.8,
          totalRatings: 25,
          address: 'Pedro Aguirre Cerda 393, Futaleufú, Chile'
        });
        
        setReviews(getStaticReviews());
      } finally {
        setLoading(false);
      }
    };
    
    fetchReviews();
  }, []);

  // Función para ir a la siguiente diapositiva
  const nextSlide = () => {
    if (!reviews.length) return;
    
    setCurrentSlide((prev) => 
      prev === Math.min(reviews.length - 1, MAX_REVIEWS - 1) ? 0 : prev + 1
    );
  };

  // Función para ir a la diapositiva anterior
  const prevSlide = () => {
    if (!reviews.length) return;
    
    setCurrentSlide((prev) => 
      prev === 0 ? Math.min(reviews.length - 1, MAX_REVIEWS - 1) : prev - 1
    );
  };

  // Renderizar estrellas según la calificación
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    // Estrellas completas
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`} className="star">★</span>);
    }
    
    // Estrella media si aplica
    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">★</span>);
    }
    
    // Estrellas vacías
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star empty">☆</span>);
    }
    
    return stars;
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Acortar texto si es muy largo
  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Reseñas estáticas como respaldo en caso de fallo de la API
  const getStaticReviews = () => {
    return [
      {
        author_name: "María González",
        profile_photo_url: "https://ui-avatars.com/api/?name=MG&background=random",
        rating: 5,
        text: "Excelente hospedaje en Futaleufú. Las habitaciones son muy cómodas y limpias. El personal es muy amable y servicial. Totalmente recomendado para descansar después de disfrutar de las actividades al aire libre.",
        time: Date.now() / 1000 - 3600 * 24 * 5 // 5 días atrás
      },
      {
        author_name: "Carlos Rodríguez",
        profile_photo_url: "https://ui-avatars.com/api/?name=CR&background=random",
        rating: 4,
        text: "Muy buen lugar, bien ubicado y con una buena relación calidad-precio. El único detalle es que el WiFi a veces fallaba un poco, pero nada grave.",
        time: Date.now() / 1000 - 3600 * 24 * 15 // 15 días atrás
      },
      {
        author_name: "Alejandra Vargas",
        profile_photo_url: "https://ui-avatars.com/api/?name=AV&background=random",
        rating: 5,
        text: "¡Lugar increíble! Me encantó la atención personalizada y la limpieza de las habitaciones. La cocina común está muy bien equipada y el café que ofrecen por la mañana es delicioso.",
        time: Date.now() / 1000 - 3600 * 24 * 25 // 25 días atrás
      },
      {
        author_name: "Pedro Sánchez",
        profile_photo_url: "https://ui-avatars.com/api/?name=PS&background=random",
        rating: 5,
        text: "Hermoso lugar en pleno corazón de Futaleufú. Habitaciones muy limpias y cómodas. El personal súper atento. Recomendado 100%.",
        time: Date.now() / 1000 - 3600 * 24 * 45 // 45 días atrás
      },
      {
        author_name: "Lucía Martínez",
        profile_photo_url: "https://ui-avatars.com/api/?name=LM&background=random",
        rating: 5,
        text: "Nos encantó nuestra estadía en la Hostería Futaleufú. El lugar es hermoso, las habitaciones son espaciosas y muy limpias. El personal es muy amable y servicial. Volveremos sin duda.",
        time: Date.now() / 1000 - 3600 * 24 * 60 // 60 días atrás
      },
      {
        author_name: "José Pereira",
        profile_photo_url: "https://ui-avatars.com/api/?name=JP&background=random",
        rating: 4,
        text: "Excelente ubicación, cerca de todo. Las habitaciones son confortables y el personal muy atento. Lo recomiendo para quienes quieren disfrutar de Futaleufú.",
        time: Date.now() / 1000 - 3600 * 24 * 75 // 75 días atrás
      },
      {
        author_name: "Ana Silva",
        profile_photo_url: "https://ui-avatars.com/api/?name=AS&background=random",
        rating: 5,
        text: "La mejor opción en Futaleufú sin duda. Habitaciones impecables, desayuno delicioso y la atención es inmejorable. Volveremos con toda seguridad.",
        time: Date.now() / 1000 - 3600 * 24 * 90 // 90 días atrás
      },
      {
        author_name: "Roberto Núñez",
        profile_photo_url: "https://ui-avatars.com/api/?name=RN&background=random",
        rating: 5,
        text: "Una experiencia maravillosa. La hostería es preciosa, las habitaciones muy cómodas y el personal súper atento. Lo recomiendo para todos los que visiten Futaleufú.",
        time: Date.now() / 1000 - 3600 * 24 * 105 // 105 días atrás
      }
    ];
  };

  return (
    <section className="guest-reviews-section" aria-label="Opiniones de huéspedes">
      <h2>Lo que dicen nuestros huéspedes</h2>
      
      {loading ? (
        <div className="reviews-loading">Cargando reseñas...</div>
      ) : (
        <div className="guest-reviews-container">
          {error && (
            <div className="reviews-notice">
              <p>Mostrando reseñas de ejemplo. Las reseñas reales están disponibles en Google Maps.</p>
            </div>
          )}
          
          <div className="reviews-content">
            {placeInfo && (
              <div className="reviews-header">
                <h3>{placeInfo.name}</h3>
                <div className="reviews-rating">
                  <div className="stars-container">
                    {renderStars(placeInfo.rating)}
                  </div>
                  <span className="rating-number">{placeInfo.rating}</span>
                  <span className="reviews-count">
                    ({placeInfo.totalRatings} opiniones)
                  </span>
                </div>
                <a 
                  href={`https://search.google.com/local/reviews?placeid=${PLACE_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-all-link"
                >
                  Ver todas las reseñas en Google
                </a>
              </div>
            )}
            
            {isMobile ? (
              <div className="reviews-carousel" ref={carouselRef}>
                <button 
                  className="carousel-btn prev-btn" 
                  onClick={prevSlide}
                  aria-label="Reseña anterior"
                >
                  &#10094;
                </button>
                
                <div className="carousel-container">
                  {reviews.slice(0, MAX_REVIEWS).map((review, index) => (
                    <div 
                      key={index} 
                      className={`carousel-item ${index === currentSlide ? 'active' : ''}`}
                    >
                      <ReviewCard 
                        review={review} 
                        placeId={PLACE_ID}
                        renderStars={renderStars} 
                        formatDate={formatDate} 
                        truncateText={truncateText} 
                      />
                    </div>
                  ))}
                </div>
                
                <button 
                  className="carousel-btn next-btn" 
                  onClick={nextSlide}
                  aria-label="Siguiente reseña"
                >
                  &#10095;
                </button>
                
                <div className="carousel-dots">
                  {reviews.slice(0, MAX_REVIEWS).map((_, index) => (
                    <span 
                      key={index} 
                      className={`dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                    ></span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="reviews-grid">
                {reviews.slice(0, MAX_REVIEWS).map((review, index) => (
                  <ReviewCard 
                    key={index} 
                    review={review} 
                    placeId={PLACE_ID}
                    renderStars={renderStars} 
                    formatDate={formatDate} 
                    truncateText={truncateText} 
                  />
                ))}
              </div>
            )}
            
            <div className="reviews-footer">
              <a 
                href={`https://search.google.com/local/reviews?placeid=${PLACE_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="view-more-btn"
              >
                Ver más reseñas
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Componente para cada tarjeta de reseña
const ReviewCard = ({ review, placeId, renderStars, formatDate, truncateText }) => {
  return (
    <div className="review-item">
      <div className="review-top">
        <img 
          src={review.profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=random`} 
          alt={`Foto de ${review.author_name}`}
          className="reviewer-photo" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(review.author_name)}&background=random`;
          }}
        />
        <div className="reviewer-info">
          <h4>{review.author_name}</h4>
          <div className="reviewer-stars">
            {renderStars(review.rating)}
          </div>
          <span className="review-time">{formatDate(review.time)}</span>
        </div>
      </div>
      <div className="review-text">
        <p>{truncateText(review.text, 150)}</p>
        {review.text.length > 150 && (
          <a 
            href={`https://search.google.com/local/reviews?placeid=${placeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="read-more"
          >
            Leer más
          </a>
        )}
      </div>
    </div>
  );
};

export default GuestReviews; 